import React, { useState } from 'react';
import { MagnifyingGlassIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import knowledgeService, { SearchResult } from '../../services/knowledgeService';

const SearchTester: React.FC = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [threshold, setThreshold] = useState(0.7);

  const handleSearch = async () => {
    if (!query.trim()) {
      toast.error('Please enter a search query');
      return;
    }

    setIsSearching(true);
    setHasSearched(false);

    try {
      const response = await knowledgeService.testSearch(query, 5, threshold);
      setResults(response.results);
      setHasSearched(true);
      
      if (response.results.length === 0) {
        toast('No relevant documents found. Try adjusting the similarity threshold.', {
          icon: 'ℹ️',
        });
      } else {
        toast.success(`Found ${response.results.length} relevant chunk${response.results.length > 1 ? 's' : ''}`);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Search failed');
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSearching) {
      handleSearch();
    }
  };

  const getSimilarityColor = (similarity: number) => {
    if (similarity >= 0.9) return 'text-green-600 bg-green-50';
    if (similarity >= 0.8) return 'text-blue-600 bg-blue-50';
    if (similarity >= 0.7) return 'text-yellow-600 bg-yellow-50';
    return 'text-gray-600 bg-gray-50';
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Test Knowledge Search</h3>
      <p className="text-sm text-gray-600 mb-4">
        Test how the AI will find information when users ask questions in the chat widget.
      </p>

      {/* Search Input */}
      <div className="space-y-4">
        <div>
          <label htmlFor="search-query" className="block text-sm font-medium text-gray-700 mb-1">
            Search Query
          </label>
          <div className="relative">
            <input
              type="text"
              id="search-query"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="e.g., What is your tuition? Do you support autism?"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
          </div>
        </div>

        {/* Threshold Slider */}
        <div>
          <label htmlFor="threshold" className="block text-sm font-medium text-gray-700 mb-1">
            Similarity Threshold: {threshold.toFixed(2)}
          </label>
          <input
            type="range"
            id="threshold"
            min="0.5"
            max="1"
            step="0.05"
            value={threshold}
            onChange={(e) => setThreshold(parseFloat(e.target.value))}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>Less strict (0.5)</span>
            <span>More strict (1.0)</span>
          </div>
        </div>

        {/* Search Button */}
        <button
          onClick={handleSearch}
          disabled={isSearching || !query.trim()}
          className={`
            w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white
            ${isSearching || !query.trim()
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
            }
          `}
        >
          {isSearching ? 'Searching...' : 'Test Search'}
        </button>
      </div>

      {/* Search Results */}
      {hasSearched && (
        <div className="mt-6">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">
            Search Results ({results.length})
          </h4>
          
          {results.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">
                No matching documents found
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Try lowering the similarity threshold or using different keywords
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {results.map((result, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className="text-sm font-medium text-gray-900">
                        {result.document}
                      </span>
                      {result.category && (
                        <span className="ml-2 text-xs text-gray-500">
                          ({result.category})
                        </span>
                      )}
                    </div>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getSimilarityColor(result.similarity)}`}>
                      {(result.similarity * 100).toFixed(1)}% match
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-3">
                    {result.text}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchTester;